import { ArrowLeft, Pencil, Star, Trash2 } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Markdown } from '@/components/ui/markdown';
import type { ApiError } from '@/core/api';
import { useEmployee } from '@/features/hrm/employees/queries';
import { employeeName } from '@/features/hrm/employees/types';
import { useMembers } from '@/features/members';
import { ReviewActionItems } from '../components/ReviewActionItems';
import { reviewStatusLabel, reviewStatusVariant } from '../constants';
import { useDeleteReview, useReview } from '../queries';

export function ReviewDetailPage() {
  const { publicId = '' } = useParams();
  const navigate = useNavigate();

  const { data: review, isPending, isError, error } = useReview(publicId);
  const employee = useEmployee(review?.employee ?? -1);
  const { data: membersData } = useMembers();
  const del = useDeleteReview();

  async function handleDelete() {
    if (!review) return;
    await del.mutateAsync(review.public_id);
    navigate('/hrm/reviews');
  }

  if (isPending) {
    return (
      <div className="max-w-3xl space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/hrm/reviews')}>
          <ArrowLeft /> Back
        </Button>
        <p className="text-destructive">{(error as unknown as ApiError).message}</p>
      </div>
    );
  }

  const fmtDate = (d: string | null) => (d ? new Date(d).toLocaleDateString() : '—');
  const employeeLabel = employee.data ? employeeName(employee.data) : `#${review.employee}`;
  const reviewerLabel = review.reviewer
    ? (membersData?.results.find((m) => m.user.id === review.reviewer)?.user.full_name ?? '…')
    : '—';

  return (
    <div className="max-w-3xl space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/hrm/reviews')}>
            <ArrowLeft />
          </Button>
          <h1 className="text-2xl font-semibold tracking-tight">{employeeLabel}</h1>
          <Badge variant={reviewStatusVariant(review.status)}>
            {reviewStatusLabel(review.status)}
          </Badge>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate(`/hrm/reviews/${review.public_id}/edit`)}>
            <Pencil /> Edit
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive">
                <Trash2 /> Delete
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete review?</AlertDialogTitle>
                <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleDelete} disabled={del.isPending}>
                  {del.isPending ? 'Deleting…' : 'Delete'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Review</CardTitle>
        </CardHeader>
        <CardContent className="text-muted-foreground grid gap-4 text-sm sm:grid-cols-3">
          <Field label="Employee">{employeeLabel}</Field>
          <Field label="Reviewer">{reviewerLabel}</Field>
          <Field label="Period">{review.period || '—'}</Field>
          <Field label="Rating">
            {review.rating != null ? (
              <span className="flex items-center gap-1">
                <Star className="size-4 fill-current text-amber-500" />
                {review.rating}
                <span className="text-muted-foreground">/5</span>
              </span>
            ) : (
              '—'
            )}
          </Field>
          <Field label="Status">{reviewStatusLabel(review.status)}</Field>
          <Field label="Review date">{fmtDate(review.review_date)}</Field>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Summary</CardTitle>
        </CardHeader>
        <CardContent className="text-sm">
          {review.summary ? (
            <Markdown>{review.summary}</Markdown>
          ) : (
            <span className="text-muted-foreground">No summary.</span>
          )}
        </CardContent>
      </Card>

      <ReviewActionItems reviewId={review.id} items={review.action_items} />
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-xs font-medium uppercase">{label}</p>
      <div className="text-foreground mt-1">{children}</div>
    </div>
  );
}
